import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useArealplaner,
    usePlanforhold,
    useCreatePlanforholdPar,
    useUpdatePlanforholdPar,
    useDeletePlanforholdPar,
    planforholdSettFra,
    type Forholdtype,
    type PlanforholdInput,
} from '@origo/planregister';
import { Notice } from '@origo/origo-ui';
import PlanDetailSkeleton from '../../components/PlanDetailSkeleton';
import { useToast } from '@origo/origo-components';
import RelationsPage from './RelationsPage';
import type { RelationEditDialogOptions, RelationFieldErrors } from './components/RelationEditDialog';
import { forholdtypeOptions } from '../../codelists/forholdtype';
import {
    buildPartyLookup,
    toRelationPair,
    visibleRelations,
} from '../../mappers/toRelations';
import type { RelationFormValues, SelectOption } from '../../types';

interface RelationsContainerProps {
    napId: string;
}

const initialDialog: RelationFormValues = {
    motpartPlanId: '',
    type: '',
    vedtakEndeligPlanDato: '',
    begrunnelse: '',
};

const RelationsContainer: React.FC<RelationsContainerProps> = ({ napId }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const planforhold = usePlanforhold(napId);
    const allPlans = useArealplaner();
    const createRelationPair = useCreatePlanforholdPar(napId);
    const updateRelationPair = useUpdatePlanforholdPar();
    const removeRelationPair = useDeletePlanforholdPar();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<RelationFormValues>(initialDialog);
    const [fieldErrors, setFieldErrors] = useState<RelationFieldErrors>({});
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const lookup = useMemo(() => buildPartyLookup(allPlans.data ?? []), [allPlans.data]);

    // Planforhold lagres parvis (A→B og speilet B→A); visibleRelations viser
    // hvert par én gang uten å skjule ensrettede «Til»-relasjoner.
    const pairs = useMemo(
        () => visibleRelations(planforhold.data ?? [], napId)
            .map(rel => toRelationPair(rel, lookup, t)),
        [planforhold.data, napId, lookup, t]
    );

    const motpartOptions: SelectOption[] = useMemo(
        () => (allPlans.data ?? [])
            .filter(p => p.nasjonalArealplanId !== napId)
            .map(p => ({ value: p.nasjonalArealplanId, label: `${p.plannavn} — ${p.planId}` })),
        [allPlans.data, napId]
    );

    const dialogOptions: RelationEditDialogOptions = useMemo(
        () => ({
            motpart: motpartOptions,
            type: forholdtypeOptions(t),
        }),
        [motpartOptions, t]
    );

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingId(null);
        setForm(initialDialog);
        setFieldErrors({});
    };

    const handleSave = async () => {
        const errors: RelationFieldErrors = {
            ...(!form.motpartPlanId && { motpartPlanId: t('relations.errors.motpartRequired') }),
            ...(!form.type && { type: t('relations.errors.typeRequired') }),
        };
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const editing = editingId
            ? planforhold.data?.find(p => p.relasjonId === editingId)
            : undefined;
        const input: PlanforholdInput = {
            relasjonId: editing?.relasjonId ?? crypto.randomUUID(),
            nasjonalArealplanIdFra: napId,
            nasjonalArealplanIdTil: form.motpartPlanId,
            forholdtype: form.type as Forholdtype,
            vedtakEndeligPlanDato: form.vedtakEndeligPlanDato ? new Date(form.vedtakEndeligPlanDato) : undefined,
            begrunnelse: form.begrunnelse.trim() || undefined,
        };

        if (editing) {
            // Oppdaterer raden og speilet parvis. En ensrettet «Til»-relasjon
            // lagres fra denne planens side (Fra = napId, motsatt type), som
            // uttrykker samme relasjon.
            const saved = await toast.execute(
                () => updateRelationPair.mutateAsync({ eksisterende: editing, input }),
                { success: t('common.saved'), error: t('common.errorSaving') }
            );
            if (saved) closeDialog();
            return;
        }

        const created = await toast.execute(() => createRelationPair.mutateAsync(input), {
            success: t('relations.created'),
            error: t('common.errorSaving'),
        });
        if (created) closeDialog();
    };

    const pendingDelete = pendingDeleteId
        ? pairs.find(p => p.id === pendingDeleteId) ?? null
        : null;

    const confirmDelete = async () => {
        const rel = planforhold.data?.find(p => p.relasjonId === pendingDeleteId);
        if (!rel) return;
        // Sletter raden og speilet parvis (F4.2.1). mutateAsync gir void — pakk
        // inn i `true` så vi kan skille suksess fra feil.
        const ok = await toast.execute(
            async () => {
                await removeRelationPair.mutateAsync(rel);
                return true;
            },
            { success: t('relations.deleted'), error: t('relations.errorDeleting') }
        );
        if (ok) setPendingDeleteId(null);
    };

    if (planforhold.isLoading || allPlans.isLoading) {
        return <PlanDetailSkeleton title={t('relations.title')} subtitle={t('relations.subtitle')} variant="list" />;
    }
    if (planforhold.isError || allPlans.isError) {
        return <Notice variant="danger">{t('common.errorLoading')}</Notice>;
    }

    return (
        <RelationsPage
            pairs={pairs}
            dialogOpen={dialogOpen}
            dialogMode={editingId ? 'edit' : 'create'}
            dialogValues={form}
            dialogOptions={dialogOptions}
            dialogFieldErrors={fieldErrors}
            isSaving={createRelationPair.isPending || updateRelationPair.isPending}
            onCreate={() => {
                setEditingId(null);
                setForm(initialDialog);
                setFieldErrors({});
                setDialogOpen(true);
            }}
            onEdit={id => {
                const rel = planforhold.data?.find(p => p.relasjonId === id);
                const view = rel ? planforholdSettFra(rel, napId) : undefined;
                setEditingId(id);
                setForm({
                    motpartPlanId: view?.motpartPlanId ?? '',
                    type: view?.forholdtype ?? '',
                    vedtakEndeligPlanDato: rel?.vedtakEndeligPlanDato?.toISOString().slice(0, 10) ?? '',
                    begrunnelse: rel?.begrunnelse ?? '',
                });
                setFieldErrors({});
                setDialogOpen(true);
            }}
            onDialogChange={(field, value) => {
                setForm(prev => ({ ...prev, [field]: value }));
                setFieldErrors(prev => ({ ...prev, [field]: undefined }));
            }}
            onDialogClose={closeDialog}
            onDialogSave={handleSave}
            onDeleteRequest={setPendingDeleteId}
            pendingDelete={pendingDelete}
            onDeleteConfirm={confirmDelete}
            onDeleteCancel={() => setPendingDeleteId(null)}
            isDeleting={removeRelationPair.isPending}
        />
    );
};

export default RelationsContainer;
